<script setup>
import { computed, onActivated, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import SettingsLayout from '../SettingsLayout.vue';
import BackButton from '../../../../components/widgets/BackButton.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FormFieldsEditor from './components/FormFieldsEditor.vue';
import FormThemeEditor from './components/FormThemeEditor.vue';
import FormWelcomeEditor from './components/FormWelcomeEditor.vue';
import FormDestinationEditor from './components/FormDestinationEditor.vue';
import FormSuccessEditor from './components/FormSuccessEditor.vue';
import FormConsentEditor from './components/FormConsentEditor.vue';
import FormIntegrationsEditor from './components/FormIntegrationsEditor.vue';
import FormPublishPanel from './components/FormPublishPanel.vue';
import FormVariantsEditor from './components/FormVariantsEditor.vue';
import FormAnalytics from './components/FormAnalytics.vue';
import FormLivePreview from './components/FormLivePreview.vue';
import { getFormTemplateById } from './templates';
import { buildPayload } from './helpers/buildPayload';

const props = defineProps({
  formId: { type: [String, Number], default: null },
});

const store = useStore();
const getters = useStoreGetters();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// Tab bar order, agrupada pela etapa do trabalho: primeiro a cara do
// formulário (Aparência), depois o conteúdo que o visitante percorre
// (Boas-vindas → Campos → Sucesso → Consentimento), então a configuração de
// para onde o lead vai (Destino → Rastreamento e integrações), e por fim
// colocar no ar e medir (Publicar → Teste A/B → Resultados). A aba inicial
// continua sendo 'fields' — é onde a maioria das edições começa.
const TABS = [
  'appearance',
  'welcome',
  'fields',
  'success',
  'consent',
  'destination',
  'integrations',
  'publish',
  'abtest',
  'analytics',
];
const activeTab = ref('fields');
const isLoading = ref(true);
const isSaving = ref(false);

const defaultTheme = () => ({
  primary_color: '#1F93FF',
  button_color: '#1F93FF',
  button_text_color: '#FFFFFF',
  background_color: '#FFFFFF',
  text_color: '#1C1C28',
  logo_url: '',
  header_image_url: '',
  // Altura (px) da imagem de topo — slider na aba Aparência.
  header_size: 72,
  radius: 16,
  font: 'sans',
  // 'single' (one page) or 'steps' (one field per step, or grouped screens
  // via field.group_with_previous). A layout choice only — same
  // fields/submission contract either way. Kept inside theme (JSONB)
  // rather than a dedicated column/migration. New forms default to
  // 'steps' (Typeform-style); existing/saved forms are unaffected since
  // record.theme.layout always overrides this default in applyRecord().
  layout: 'steps',
  // Optional SpotForm/Typeform-style cover screen shown before the first
  // question. Disabled by default; same "no migration" trick as layout
  // above — lives inside theme (JSONB).
  welcome: {
    enabled: false,
    title: '',
    description: '',
    button_text: '',
  },
  // Customizable title shown on the success screen (theme.success_title).
  // Same "no migration" trick as layout/welcome above; falls back to a
  // default when blank (see App.vue).
  success_title: '',
});

const defaultFields = () => [
  {
    key: 'name',
    type: 'text',
    label: 'Nome',
    required: true,
    placeholder: '',
    options: [],
    group_with_previous: false,
  },
  {
    key: 'email',
    type: 'email',
    label: 'Email',
    required: true,
    placeholder: '',
    options: [],
    group_with_previous: false,
  },
  {
    key: 'phone',
    type: 'phone',
    label: 'Telefone',
    required: true,
    placeholder: '',
    options: [],
    group_with_previous: false,
  },
];

// F3.1 — config da nutrição por IA ao receber o lead (coluna dedicada, não
// vaza no payload público como o theme). whatsapp_inbox_id define de qual inbox
// WhatsApp a Lia inicia a conversa; message_content é o texto-base da abertura
// (a IA personaliza); template_id só é usado em inbox Meta oficial (HSM).
const defaultAiNurture = () => ({
  enabled: false,
  whatsapp_inbox_id: '',
  message_content: '',
  template_id: '',
});

// Integrações do form: rastreamento (UTMs + params extras), Meta CAPI, aviso
// WhatsApp. Colunas jsonb dedicadas (não vazam no payload público como o theme).
const defaultTrackingConfig = () => ({
  utm_enabled: true,
  capture_referrer: true,
  partial_capture_enabled: true,
  google_measurement_id: '',
  google_tag_manager_id: '',
  send_disqualified_webhook: true,
  public_files_enabled: false,
  custom_params: [],
});
const defaultMetaCapi = () => ({ enabled: false, event_name: 'Lead' });
const defaultNotify = () => ({
  enabled: false,
  whatsapp_inbox_id: '',
  recipient: '',
  recipients: [],
  message_template: '',
});

const blankForm = () => ({
  id: null,
  name: '',
  slug: '',
  fields: defaultFields(),
  theme: defaultTheme(),
  funnel_id: '',
  stage_name: '',
  inbox_id: null,
  consent_enabled: true,
  consent_text: '',
  success_mode: 'message',
  success_message: '',
  redirect_url: '',
  enabled: true,
  ai_nurture: defaultAiNurture(),
  variants: [],
  public_url: '',
  webhook_url: '',
  custom_domain: '',
  custom_hostname_state: {},
  custom_hostname_target: '',
  tracking_config: defaultTrackingConfig(),
  meta_capi: defaultMetaCapi(),
  notify: defaultNotify(),
  outbound_webhook_url: '',
  outbound_webhook_secret: '',
  scoring: {},
});

const form = ref(blankForm());

// Slug editável: auto-normaliza (minúsculo, sem acento, só a-z0-9- e hífens).
const slug = computed({
  get: () => form.value.slug,
  set: value => {
    form.value.slug = (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/-+/g, '-');
  },
});
const formPublicPath = computed(() => `/f/${slug.value || '…'}`);
const mappingEntries = ref([]);

const isEditing = computed(() => Boolean(props.formId));
const isSaved = computed(() => Boolean(form.value.id));

const mappingToObject = () => {
  const result = {};
  mappingEntries.value.forEach(entry => {
    if (entry.payloadKey && entry.payloadKey.trim()) {
      result[entry.payloadKey.trim()] = entry.fieldKey || '';
    }
  });
  return result;
};

const applyRecord = record => {
  form.value = {
    id: record.id,
    name: record.name || '',
    slug: record.slug || '',
    fields: record.fields?.length ? record.fields : defaultFields(),
    theme: { ...defaultTheme(), ...(record.theme || {}) },
    funnel_id: record.funnel_id || '',
    stage_name: record.stage_name || '',
    inbox_id: record.inbox_id || null,
    consent_enabled: record.consent_enabled ?? true,
    consent_text: record.consent_text || '',
    success_mode: record.success_mode || 'message',
    success_message: record.success_message || '',
    redirect_url: record.redirect_url || '',
    enabled: record.enabled ?? true,
    ai_nurture: { ...defaultAiNurture(), ...(record.ai_nurture || {}) },
    variants: record.variants || [],
    public_url: record.public_url
      ? new URL(record.public_url, window.location.origin).toString()
      : '',
    webhook_url: record.webhook_url
      ? `${window.location.origin}${record.webhook_url}`
      : '',
    tracking_config: {
      ...defaultTrackingConfig(),
      ...(record.tracking_config || {}),
    },
    meta_capi: { ...defaultMetaCapi(), ...(record.meta_capi || {}) },
    notify: { ...defaultNotify(), ...(record.notify || {}) },
    outbound_webhook_url: record.outbound_webhook_url || '',
    outbound_webhook_secret: record.outbound_webhook_secret || '',
    custom_domain: record.custom_domain || '',
    custom_hostname_state: record.custom_hostname_state || {},
    custom_hostname_target: record.custom_hostname_target || '',
    scoring: record.scoring || {},
  };
  mappingEntries.value = Object.entries(record.field_mapping || {}).map(
    ([payloadKey, fieldKey]) => ({ payloadKey, fieldKey })
  );
};

const applyTemplate = template => {
  form.value = {
    ...blankForm(),
    name: template.name,
    fields: template.fields.map(fieldItem => ({ ...fieldItem })),
    theme: { ...defaultTheme(), ...template.theme },
  };
};

// onActivated (não onMounted): a tela de settings é envolvida por <keep-alive>,
// então o FormEditor é cacheado e reusado entre forms_new e forms_edit. Com
// onMounted (que só roda no primeiro mount) editar um segundo form mostrava os
// dados do primeiro (instância cacheada). onActivated roda no mount inicial E a
// cada reativação do cache, recarregando sempre pelo props.formId atual.
onActivated(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      store.dispatch('forms/get'),
      store.dispatch('funnels/get'),
      // inboxes: seletor de inbox WhatsApp da nutrição por IA (aba Destino).
      store.dispatch('inboxes/get'),
    ]);
    if (isEditing.value) {
      const record = getters['forms/getFormById'].value(props.formId);
      if (record) {
        applyRecord(record);
      } else {
        useAlert(t('FORMS_MGMT.EDITOR.API.LOAD_ERROR'));
        router.push({ name: 'forms_list' });
      }
    } else {
      // Novo form: reseta o estado (a instância pode estar cacheada com um form
      // editado antes) e aplica o template escolhido, se houver.
      const template = getFormTemplateById(route.query.template);
      if (template) {
        applyTemplate(template);
      } else {
        form.value = blankForm();
      }
      mappingEntries.value = [];
    }
  } finally {
    isLoading.value = false;
  }
});

const isFormValid = computed(
  () => Boolean(form.value.name?.trim()) && form.value.fields.length > 0
);

const validationError = computed(() => {
  if (!form.value.name?.trim()) {
    return t('FORMS_MGMT.EDITOR.VALIDATION.NAME_REQUIRED');
  }
  if (!form.value.fields.length) {
    return t('FORMS_MGMT.EDITOR.VALIDATION.FIELDS_REQUIRED');
  }
  return '';
});

const goBack = () => {
  router.push({ name: 'forms_list' });
};

const saveForm = async () => {
  if (!isFormValid.value) {
    useAlert(validationError.value);
    return;
  }
  isSaving.value = true;
  try {
    const payload = buildPayload(form.value, {
      fieldMapping: mappingToObject(),
    });
    if (form.value.id) {
      const record = await store.dispatch('forms/update', {
        id: form.value.id,
        ...payload,
      });
      applyRecord(record);
      useAlert(t('FORMS_MGMT.EDITOR.API.UPDATE_SUCCESS'));
    } else {
      const record = await store.dispatch('forms/create', payload);
      applyRecord(record);
      useAlert(t('FORMS_MGMT.EDITOR.API.CREATE_SUCCESS'));
      router.replace({ name: 'forms_edit', params: { formId: record.id } });
    }
  } catch (error) {
    useAlert(t('FORMS_MGMT.EDITOR.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const tabLabel = tabKey =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(`FORMS_MGMT.EDITOR.TABS.${tabKey.toUpperCase()}`);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('FORMS_MGMT.EDITOR.LOADING')"
  >
    <template #header>
      <div class="flex flex-col gap-2 w-full">
        <BackButton
          compact
          :button-label="$t('FORMS_MGMT.EDITOR.BACK')"
          :back-url="{ name: 'forms_list' }"
        />
        <div class="flex items-center justify-between w-full gap-4">
          <h1 class="text-xl font-medium tracking-tight text-n-slate-12">
            {{
              isEditing
                ? $t('FORMS_MGMT.EDITOR.EDIT_TITLE')
                : $t('FORMS_MGMT.EDITOR.CREATE_TITLE')
            }}
          </h1>
          <div class="flex items-center gap-2">
            <Button
              faded
              slate
              :label="$t('FORMS_MGMT.EDITOR.CANCEL')"
              @click="goBack"
            />
            <Button
              :label="$t('FORMS_MGMT.EDITOR.SAVE')"
              :is-loading="isSaving"
              :disabled="!isFormValid"
              @click="saveForm"
            />
          </div>
        </div>
      </div>
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <Input
          v-model="form.name"
          :label="$t('FORMS_MGMT.EDITOR.NAME.LABEL')"
          :placeholder="$t('FORMS_MGMT.EDITOR.NAME.PLACEHOLDER')"
          class="max-w-md"
        />

        <div class="flex max-w-md flex-col gap-1">
          <Input
            v-model="slug"
            :label="$t('FORMS_MGMT.EDITOR.SLUG.LABEL')"
            :placeholder="$t('FORMS_MGMT.EDITOR.SLUG.PLACEHOLDER')"
          />
          <span class="text-xs text-n-slate-10">
            {{ $t('FORMS_MGMT.EDITOR.SLUG.HINT') }}
            <span class="font-medium text-n-slate-11">{{
              formPublicPath
            }}</span>
          </span>
        </div>

        <div
          class="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start"
        >
          <div class="flex flex-col gap-6 min-w-0">
            <div
              class="flex items-center gap-1 border-b border-n-weak overflow-x-auto"
            >
              <button
                v-for="tabKey in TABS"
                :key="tabKey"
                type="button"
                class="px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors"
                :class="
                  activeTab === tabKey
                    ? 'border-n-brand text-n-brand'
                    : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
                "
                @click="activeTab = tabKey"
              >
                {{ tabLabel(tabKey) }}
              </button>
            </div>

            <div class="border border-n-weak rounded-md p-6 bg-n-alpha-1">
              <FormFieldsEditor
                v-if="activeTab === 'fields'"
                v-model="form.fields"
                v-model:scoring="form.scoring"
              />
              <FormThemeEditor
                v-else-if="activeTab === 'appearance'"
                v-model="form.theme"
                :form-id="form.id"
              />
              <FormWelcomeEditor
                v-else-if="activeTab === 'welcome'"
                v-model="form.theme"
              />
              <FormDestinationEditor
                v-else-if="activeTab === 'destination'"
                v-model:funnel-id="form.funnel_id"
                v-model:stage-name="form.stage_name"
                v-model:inbox-id="form.inbox_id"
                v-model:ai-nurture="form.ai_nurture"
                v-model:scoring="form.scoring"
                :fields="form.fields"
              />
              <FormSuccessEditor
                v-else-if="activeTab === 'success'"
                v-model="form.theme"
                v-model:success-mode="form.success_mode"
                v-model:success-message="form.success_message"
                v-model:redirect-url="form.redirect_url"
              />
              <FormConsentEditor
                v-else-if="activeTab === 'consent'"
                v-model:consent-enabled="form.consent_enabled"
                v-model:consent-text="form.consent_text"
              />
              <FormIntegrationsEditor
                v-else-if="activeTab === 'integrations'"
                v-model:tracking-config="form.tracking_config"
                v-model:meta-capi="form.meta_capi"
                v-model:notify="form.notify"
                v-model:webhook-url="form.outbound_webhook_url"
                v-model:webhook-secret="form.outbound_webhook_secret"
              />
              <FormPublishPanel
                v-else-if="activeTab === 'publish'"
                v-model:enabled="form.enabled"
                v-model:mapping-entries="mappingEntries"
                v-model:custom-domain="form.custom_domain"
                :fields="form.fields"
                :public-url="form.public_url"
                :webhook-url="form.webhook_url"
                :is-saved="isSaved"
                :form-name="form.name"
                :custom-hostname-state="form.custom_hostname_state"
                :custom-hostname-target="form.custom_hostname_target"
              />
              <FormVariantsEditor
                v-else-if="activeTab === 'abtest'"
                v-model="form.variants"
              />
              <FormAnalytics
                v-else-if="activeTab === 'analytics'"
                :form-id="form.id"
              />
            </div>
          </div>

          <div class="flex flex-col gap-2 xl:sticky xl:top-6">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FORMS_MGMT.EDITOR.PREVIEW.TITLE') }}
            </span>
            <FormLivePreview
              :name="form.name"
              :fields="form.fields"
              :theme="form.theme"
              :consent-enabled="form.consent_enabled"
              :consent-text="form.consent_text"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
